import { useEffect, useState } from "react";
import { Package, Loader2, Check, Truck, Store as StoreIcon, RefreshCw, Mail, Send } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const NotifyControl = ({ order, onSent }) => {
  const kind = order.shipping_cents > 0 ? "shipped" : "pickup";
  const label = kind === "shipped" ? "Shipped" : "Ready for Pickup";
  const placeholder = kind === "shipped" ? "Add a tracking number or note (optional)" : "Add pickup time/location (optional)";
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/notify`, { type: kind, note });
      toast.success(`"${label}" email sent to ${(order.customer_name || "").split(" ")[0] || "customer"}`);
      onSent(data);
      setOpen(false);
      setNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 border-t border-line pt-3" data-testid={`notify-block-${order.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen((o) => !o)} data-testid={`notify-toggle-${order.id}`}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-[0.1em] border border-navy/30 text-navy/70 hover:bg-navy hover:text-white hover:border-navy transition-colors">
          <Mail size={14} /> EMAIL: {label.toUpperCase()}
        </button>
        {order.last_notified_at && (
          <span className="text-[11px] text-green-700 flex items-center gap-1" data-testid={`notify-status-${order.id}`}>
            <Check size={12} /> {order.last_notification_type === "shipped" ? "Shipped" : "Pickup"} email sent {fmtDate(order.last_notified_at)}
          </span>
        )}
      </div>
      {open && (
        <div className="mt-3 bg-paper border border-line p-3" data-testid={`notify-form-${order.id}`}>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid={`notify-note-${order.id}`} rows={2} placeholder={placeholder}
            className="w-full border border-line px-3 py-2 text-sm outline-none focus:border-navy resize-none bg-white" />
          <div className="flex items-center gap-2 mt-2">
            <button onClick={send} disabled={sending} data-testid={`notify-send-${order.id}`}
              className="flex items-center gap-1.5 bg-gold text-ink px-4 py-2 text-xs font-bold tracking-[0.1em] hover:bg-gold-soft transition-colors disabled:opacity-60">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} SEND EMAIL
            </button>
            <button onClick={() => setOpen(false)} className="text-navy/50 text-xs hover:text-navy px-2">Cancel</button>
          </div>
          <p className="text-navy/40 text-[11px] mt-2">Sends to {order.customer_email}. Marks this order as fulfilled.</p>
        </div>
      )}
    </div>
  );
};

export const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleFulfilled = async (o) => {
    try {
      await api.put(`/admin/orders/${o.id}/fulfill`, { fulfilled: !o.fulfilled });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, fulfilled: !x.fulfilled } : x)));
      toast.success(!o.fulfilled ? "Marked as fulfilled" : "Marked as not fulfilled");
    } catch {
      toast.error("Failed to update.");
    }
  };

  const revenue = orders.reduce((s, o) => s + (o.amount_total_cents || 0), 0);
  const pending = orders.filter((o) => !o.fulfilled).length;

  return (
    <div data-testid="orders-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex flex-wrap gap-8">
          <div><p className="font-display text-3xl text-ink">{orders.length}</p><p className="text-navy/50 text-xs tracking-[0.12em]">PAID ORDERS</p></div>
          <div><p className="font-display text-3xl text-ink">{money(revenue)}</p><p className="text-navy/50 text-xs tracking-[0.12em]">TOTAL COLLECTED</p></div>
          <div><p className="font-display text-3xl text-gold">{pending}</p><p className="text-navy/50 text-xs tracking-[0.12em]">TO FULFILL</p></div>
        </div>
        <button onClick={load} data-testid="orders-refresh" className="flex items-center gap-2 border border-line px-4 py-2.5 text-xs font-bold tracking-[0.12em] text-navy/70 hover:border-navy hover:text-ink transition-colors">
          <RefreshCw size={14} /> REFRESH
        </button>
      </div>

      {loading ? (
        <p className="text-navy/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading orders…</p>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-line p-12 text-center text-navy/60">
          <Package size={36} className="mx-auto text-navy/20 mb-3" />
          No paid orders yet. They'll show up here the moment someone checks out.
        </div>
      ) : (
        <div className="space-y-5" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className={`bg-white border p-5 ${o.fulfilled ? "border-line" : "border-l-4 border-l-gold border-line"}`} data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-ink">{o.customer_name}</p>
                    {!o.fulfilled && <span className="bg-gold text-ink text-[10px] font-bold px-2 py-0.5 tracking-[0.1em]">NEW</span>}
                  </div>
                  <p className="text-navy/60 text-xs mt-0.5">
                    <a href={`mailto:${o.customer_email}`} className="hover:text-gold">{o.customer_email}</a>
                    {o.customer_phone ? ` · ${o.customer_phone}` : ""}
                  </p>
                  <p className="text-navy/40 text-[11px] mt-0.5">Order #{o.id.slice(0, 8)} · {fmtDate(o.paid_at || o.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl text-ink">{money(o.amount_total_cents)}</p>
                  <p className="text-navy/40 text-[11px]">total paid{o.tax_enabled ? " (incl. tax)" : ""}</p>
                </div>
              </div>

              <div className="mt-4 border-t border-line pt-3">
                {o.items.map((it, i) => (
                  <div key={i} className="py-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-navy/80">
                        {it.name}
                        {(it.size || it.color) && <span className="text-navy/40"> ({[it.size, it.color].filter(Boolean).join(" · ")})</span>}
                        <span className="text-navy/40"> × {it.quantity}</span>
                      </span>
                      <span className="text-navy/70">{money(it.unit_amount * it.quantity)}</span>
                    </div>
                    {(it.personalization || it.personalization_image) && (
                      <div className="mt-1 ml-2 flex items-center gap-2 flex-wrap" data-testid={`order-pers-${o.id}-${i}`}>
                        {it.personalization && <span className="text-[11px] text-gold">✎ "{it.personalization}"</span>}
                        {it.personalization_image && (
                          <a href={resolveImg(it.personalization_image)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[11px] text-navy underline" data-testid={`order-pers-img-${o.id}-${i}`}>
                            <img src={resolveImg(it.personalization_image)} alt="Custom upload" className="w-9 h-9 object-cover border border-line" />
                            Download photo
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {o.shipping_cents > 0 && (
                  <div className="flex items-center justify-between text-sm py-1 text-navy/60">
                    <span>Shipping</span><span>{money(o.shipping_cents)}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3">
                <div className="text-xs text-navy/60 flex items-center gap-1.5">
                  {o.shipping_cents > 0 ? <><Truck size={13} /> Ship to:</> : <><StoreIcon size={13} /> School pickup</>}
                  {o.shipping_cents > 0 && (o.shipping_address ? (
                    <span className="text-navy/80">
                      {[o.shipping_address.line1, o.shipping_address.line2, o.shipping_address.city, o.shipping_address.state, o.shipping_address.postal_code].filter(Boolean).join(", ")}
                    </span>
                  ) : <span className="text-navy/40 italic">address pending</span>)}
                </div>
                <button onClick={() => toggleFulfilled(o)} data-testid={`fulfill-${o.id}`}
                  className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-[0.12em] transition-colors ${o.fulfilled ? "bg-green-600 text-white hover:bg-green-700" : "border border-navy/30 text-navy/70 hover:bg-ink hover:text-white hover:border-ink"}`}>
                  <Check size={14} /> {o.fulfilled ? "FULFILLED" : "MARK FULFILLED"}
                </button>
              </div>

              <NotifyControl order={o} onSent={(d) => setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, fulfilled: true, last_notified_at: d.sent_at, last_notification_type: d.type } : x)))} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
